"use client";

import { CSSProperties, useEffect, useState } from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    StepItem,
    StepsAppearance,
    StepsBlock as StepsBlockType,
} from "@/lib/utils/lomdaV3/types";
import {
    findBlockDeep,
    useLomdaV3Editor,
} from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import { resolveStepsAppearance } from "../../../lib/blockRegistry";
import { DEFAULT_THEME } from "../../../lib/theme";
import RichText from "../text/richText";
import { stripHtml, wrapInTag } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import RichHtml from "./richHtml";
import NestedBlocks from "../layout/nestedBlocks";
import SlotHeaderDrop from "../dnd/slotHeaderDrop";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

const CARD_SHADOW = "shadow-[0_4px_16px_rgba(153,187,255,0.24)]";

const stepBlocks = (it: StepItem) => it.blocks ?? [];
const cardStyle = (a: StepsAppearance): CSSProperties => ({
    backgroundColor: a.cardBackground,
    borderColor: a.border,
    borderWidth: a.borderWidth,
    borderStyle: "solid",
});

export default function StepsBlock({ block }: { block: StepsBlockType }) {
    const locale = useLomdaLocale();
    const titleTag = fieldTag("steps", "title");
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);

    const editable = useBlockEditable();
    const gridGap = useResolvedGridGap(block.id);
    const items = block.data.items;
    const listOnly = block.data.display === "list";
    const theme = useLomdaV3Editor((s) => s.metadata.theme) ?? DEFAULT_THEME;
    const appearance = resolveStepsAppearance(block.data.appearance, theme);
    const numberColor = theme.primary;
    const activeNumberColor = theme.primaryForeground;
    // The preview keeps its own open step; the editor's lives in the store so
    // Add and "Go to block" can see and open it.
    const [active, setActive] = useState(0);
    const openId = useLomdaV3Editor((s) => s.openSlots[block.id]);
    const setOpenSlot = useLomdaV3Editor((s) => s.setOpenSlot);
    const open = (id: string) => setOpenSlot(block.id, id);

    // List only hides every step's content, so a selection in there (left by
    // the switch or an undo) moves to the steps block.
    const setSelectedBlockId = useLomdaV3Editor((s) => s.setSelectedBlockId);
    const hiddenSelection = useLomdaV3Editor(
        (s) =>
            editable &&
            listOnly &&
            s.selectedBlockId !== block.id &&
            !!findBlockDeep([block], s.selectedBlockId)
    );
    useEffect(() => {
        if (hiddenSelection) setSelectedBlockId(block.id);
    }, [hiddenSelection, block.id, setSelectedBlockId]);

    const setData = (patch: Partial<StepsBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const setItems = (next: StepItem[]) => setData({ items: next });
    const patchItem = (id: string, patch: Partial<StepItem>) =>
        setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    const titleFor = (it: StepItem, i: number) =>
        stripHtml(it.title) ||
        t("lomda-v3-editor.steps.step-label", locale, { number: i + 1 });

    // ── Preview ──
    if (!editable) {
        const filled = items.filter(
            (it) =>
                stripHtml(it.title) ||
                stripHtml(it.subtitle) ||
                (!listOnly && (it.contentHeading || stepBlocks(it).length))
        );
        if (filled.length === 0) return null;
        const current = filled[Math.min(active, filled.length - 1)] ?? filled[0];
        return (
            <div className="flex flex-col gap-10">
                {filled.length > 0 && (
                    <div
                        className={cn(
                            "grid grid-cols-1 gap-8",
                            !listOnly && "md:grid-cols-2 md:gap-12"
                        )}
                    >
                        <Timeline
                            items={filled}
                            activeId={listOnly ? undefined : current?.id}
                            onSelect={
                                listOnly
                                    ? undefined
                                    : (id) =>
                                          setActive(
                                              filled.findIndex((x) => x.id === id)
                                          )
                            }
                            titleFor={titleFor}
                            gridGap={gridGap}
                            appearance={appearance}
                            numberColor={numberColor}
                            activeNumberColor={activeNumberColor}
                        />
                        {current && !listOnly && (
                            <div
                                className={cn(
                                    "lomda-v3-card-text rounded-3xl p-8 md:p-10",
                                    CARD_SHADOW
                                )}
                                style={cardStyle(appearance)}
                            >
                                <NestedBlocks
                                    key={current.id}
                                    containerId={block.id}
                                    slotId={current.id}
                                    blocks={stepBlocks(current)}
                                    editable={false}
                                    previewGap="gap-4"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ── Editor ──
    const current = items.find((it) => it.id === openId) ?? items[0];

    return (
        <div className="flex flex-col gap-10">
            <div
                className={cn(
                    "grid grid-cols-1 gap-8",
                    !listOnly && "md:grid-cols-2 md:gap-12"
                )}
            >
                <div
                    className="relative flex flex-col"
                    style={{ gap: gridGap }}
                >
                    <span
                        aria-hidden
                        className="absolute bottom-11 top-11 w-0.5 -translate-x-1/2 bg-primary/20"
                        style={{ insetInlineStart: 52 }}
                    />
                    {items.map((it, i) => {
                        const isActive = !listOnly && it.id === current?.id;
                        const row = (
                            <div
                                key={it.id}
                                onPointerDown={
                                    listOnly ? undefined : () => open(it.id)
                                }
                                className={cn(
                                    "group/step relative z-10 flex items-start gap-5 rounded-3xl px-7 py-4 transition",
                                    isActive && ["lomda-v3-card-text", CARD_SHADOW]
                                )}
                                style={
                                    isActive ? cardStyle(appearance) : undefined
                                }
                            >
                                <StepCircle
                                    index={i}
                                    active={isActive}
                                    appearance={appearance}
                                    numberColor={numberColor}
                                    activeNumberColor={activeNumberColor}
                                />
                                <div className="flex min-w-0 flex-1 flex-col gap-1">
                                    <RichText
                                        initialHtml={
                                            it.title || wrapInTag(titleTag)
                                        }
                                        defaultTag={titleTag}
                                        editable
                                        useGlobalToolbar
                                        blockId={block.id}
                                        groupKey={`${block.id}:step-title`}
                                        placeholder={t(
                                            "lomda-v3-editor.steps.title-placeholder"
                                        )}
                                        onCommit={(title) =>
                                            patchItem(it.id, { title })
                                        }
                                    />
                                    <RichText
                                        initialHtml={it.subtitle}
                                        editable
                                        useGlobalToolbar
                                        blockId={block.id}
                                        groupKey={`${block.id}:step-subtitle`}
                                        placeholder={t(
                                            "lomda-v3-editor.steps.subtitle-placeholder"
                                        )}
                                        editorClassName="text-muted-foreground"
                                        onCommit={(subtitle) =>
                                            patchItem(it.id, { subtitle })
                                        }
                                    />
                                </div>
                                {items.length > 1 && (
                                    <button
                                        type="button"
                                        aria-label={t("lomda-v3-editor.block.delete")}
                                        onClick={() => {
                                            if (it.id === current?.id)
                                                open(
                                                    (items[i - 1] ?? items[i + 1]).id
                                                );
                                            setItems(
                                                items.filter(
                                                    (x) => x.id !== it.id
                                                )
                                            );
                                        }}
                                        className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground/50 opacity-0 transition hover:text-destructive group-hover/step:opacity-100"
                                    >
                                        <X className="size-4" />
                                    </button>
                                )}
                            </div>
                        );
                        if (listOnly) return row;
                        return (
                            <SlotHeaderDrop
                                key={it.id}
                                containerId={block.id}
                                slotId={it.id}
                                count={stepBlocks(it).length}
                                open={isActive}
                                onOpen={() => open(it.id)}
                            >
                                {row}
                            </SlotHeaderDrop>
                        );
                    })}
                </div>

                {current && !listOnly && (
                    <div
                        className={cn(
                            "lomda-v3-card-text rounded-3xl p-8 md:p-10",
                            CARD_SHADOW
                        )}
                        style={cardStyle(appearance)}
                    >
                        <NestedBlocks
                            key={current.id}
                            containerId={block.id}
                            slotId={current.id}
                            blocks={stepBlocks(current)}
                            editable
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function StepCircle({
    index,
    active,
    appearance,
    numberColor,
    activeNumberColor,
}: {
    index: number;
    active: boolean;
    appearance: StepsAppearance;
    numberColor: string;
    activeNumberColor: string;
}) {
    return (
        <span
            className="relative z-10 flex size-12 shrink-0 items-center justify-center rounded-full lomda-v3-text-h6 transition"
            style={
                active
                    ? {
                          backgroundColor: appearance.activeNumberBackground,
                          color: activeNumberColor,
                      }
                    : {
                          backgroundColor: appearance.numberBackground,
                          color: numberColor,
                      }
            }
        >
            {index + 1}
        </span>
    );
}

function Timeline({
    items,
    activeId,
    onSelect,
    titleFor,
    gridGap,
    appearance,
    numberColor,
    activeNumberColor,
}: {
    items: StepItem[];
    activeId?: string;
    /** Absent = list only: rows are static, nothing is selected. */
    onSelect?: (id: string) => void;
    titleFor: (it: StepItem, i: number) => string;
    gridGap: number;
    appearance: StepsAppearance;
    numberColor: string;
    activeNumberColor: string;
}) {
    return (
        <div className="relative flex flex-col" style={{ gap: gridGap }}>
            <span
                aria-hidden
                className="absolute bottom-11 top-11 w-0.5 -translate-x-1/2 bg-primary/20"
                style={{ insetInlineStart: 52 }}
            />
            {items.map((it, i) => {
                const isActive = it.id === activeId;
                const row = (
                    <>
                        <StepCircle
                            index={i}
                            active={isActive}
                            appearance={appearance}
                            numberColor={numberColor}
                            activeNumberColor={activeNumberColor}
                        />
                        <span className="flex min-w-0 flex-col gap-1">
                            {stripHtml(it.title) ? (
                                <RichHtml
                                    html={it.title}
                                    className="text-foreground"
                                />
                            ) : (
                                <span className="lomda-v3-text-h5 text-foreground">
                                    {titleFor(it, i)}
                                </span>
                            )}
                            {stripHtml(it.subtitle) && (
                                <RichHtml
                                    html={it.subtitle}
                                    className="text-muted-foreground"
                                />
                            )}
                        </span>
                    </>
                );
                const rowClass =
                    "relative z-10 flex items-start gap-5 rounded-3xl px-7 py-4 text-start transition";
                if (!onSelect)
                    return (
                        <div key={it.id} className={rowClass}>
                            {row}
                        </div>
                    );
                return (
                    <button
                        key={it.id}
                        type="button"
                        onClick={() => onSelect(it.id)}
                        className={cn(
                            rowClass,
                            isActive
                                ? ["lomda-v3-card-text", CARD_SHADOW]
                                : "hover:bg-card/50"
                        )}
                        style={isActive ? cardStyle(appearance) : undefined}
                    >
                        {row}
                    </button>
                );
            })}
        </div>
    );
}
